import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import {
  Cloud, Monitor, ArrowRight, ArrowLeft, CheckCircle2,
  Wifi, Home, MessageCircle, CreditCard, Globe, Send, Loader2, RotateCcw
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VersionQuiz = () => {
  const { t } = useLanguage();

  const questions = [
    {
      id: 1,
      question: t("quiz_q1"),
      options: [
        { label: t("quiz_q1_a"), desc: t("quiz_q1_a_desc"), icon: Wifi, value: "cloud" },
        { label: t("quiz_q1_b"), desc: t("quiz_q1_b_desc"), icon: Home, value: "local" },
      ],
    },
    {
      id: 2,
      question: t("quiz_q2"),
      options: [
        { label: t("quiz_q2_a"), desc: t("quiz_q2_a_desc"), icon: MessageCircle, value: "cloud" },
        { label: t("quiz_q2_b"), desc: t("quiz_q2_b_desc"), icon: MessageCircle, value: "local" },
      ],
    },
    {
      id: 3,
      question: t("quiz_q3"),
      options: [
        { label: t("quiz_q3_a"), desc: t("quiz_q3_a_desc"), icon: CreditCard, value: "cloud" },
        { label: t("quiz_q3_b"), desc: t("quiz_q3_b_desc"), icon: CreditCard, value: "local" },
      ],
    },
    {
      id: 4,
      question: t("quiz_q4"),
      options: [
        { label: t("quiz_q4_a"), desc: t("quiz_q4_a_desc"), icon: Globe, value: "cloud" },
        { label: t("quiz_q4_b"), desc: t("quiz_q4_b_desc"), icon: Monitor, value: "local" },
      ],
    },
  ];

  const resultData = {
    cloud: {
      title: t("quiz_result_cloud_title"),
      subtitle: t("quiz_result_cloud_sub"),
      color: "#06b6d4",
      icon: Cloud,
      reasons: [
        t("feat_access_cloud"),
        t("feat_whatsapp_cloud"),
        t("feat_domain_cloud"),
        t("feat_updates_cloud"),
      ],
    },
    local: {
      title: t("quiz_result_local_title"),
      subtitle: t("quiz_result_local_sub"),
      color: "#10b981",
      icon: Monitor,
      reasons: [
        t("feat_recurring_local"),
        t("feat_storage_local"),
        t("feat_install_local"),
        t("feat_access_local"),
      ],
    },
    both: {
      title: t("quiz_result_both_title"),
      subtitle: t("quiz_result_both_sub"),
      color: "#f59e0b",
      icon: CheckCircle2,
      reasons: [
        t("feat_payment_cloud"),
        t("feat_storage_local"),
        t("feat_support_cloud"),
        t("feat_install_cloud"),
      ],
    },
  };
  const [step, setStep] = useState(0); // 0=intro, 1-4=questions, 5=result, 6=lead form, 7=thanks
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);

  const handleAnswer = (value) => {
    const newAnswers = [...answers, value];
    setAnswers(newAnswers);

    if (newAnswers.length === questions.length) {
      const cloudCount = newAnswers.filter((a) => a === "cloud").length;
      setResult(cloudCount >= 3 ? "cloud" : cloudCount <= 1 ? "local" : "both");
      setStep(5);
    } else {
      setStep(step + 1);
    }
  };

  const goBack = () => {
    if (step > 1) {
      setAnswers(answers.slice(0, -1));
      setStep(step - 1);
    }
  };

  const restart = () => {
    setStep(0);
    setAnswers([]);
    setResult(null);
    setForm({ name: "", email: "", phone: "" });
  };

  const handleSubmitLead = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetch(`${API_URL}/api/demo-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          interest: `Quiz: Recomendacion ${result === "cloud" ? "Cloud" : result === "local" ? "Local" : "Ambas"}`,
          company: "",
        }),
      });
      setStep(7);
    } catch {
      setStep(7);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto mt-16" data-testid="version-quiz">
      <div className="rounded-2xl border border-white/10 bg-[#080e1c] overflow-hidden">
        <AnimatePresence mode="wait">
          {/* Intro */}
          {step === 0 && (
            <motion.div
              key="intro"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, x: -50 }}
              className="p-8 text-center"
            >
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#06b6d4]/20 to-[#10b981]/20 border border-white/10 flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 size={28} className="text-[#06b6d4]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">{t("quiz_title")}</h3>
              <p className="text-[#94a3b8] text-sm mb-6">
                {t("quiz_subtitle")}
              </p>
              <motion.button
                data-testid="quiz-start-btn"
                onClick={() => setStep(1)}
                className="inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-gradient-to-r from-[#06b6d4] to-[#10b981] text-white font-bold text-sm"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                {t("quiz_start")}
                <ArrowRight size={16} />
              </motion.button>
              <p className="text-[#475569] text-xs mt-4">{t("quiz_time")}</p>
            </motion.div>
          )}

          {/* Questions */}
          {step >= 1 && step <= 4 && (
            <motion.div
              key={`q-${step}`}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.25 }}
              className="p-8"
            >
              {/* Progress */}
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={goBack}
                  className="text-[#64748b] hover:text-white transition-colors text-sm flex items-center gap-1"
                  data-testid="quiz-back-btn"
                >
                  <ArrowLeft size={14} />
                  {t("quiz_back")}
                </button>
                <div className="flex gap-1.5">
                  {questions.map((_, i) => (
                    <div
                      key={i}
                      className="h-1.5 rounded-full transition-all duration-300"
                      style={{
                        width: i < step ? 24 : 12,
                        backgroundColor: i < step ? "#06b6d4" : "#1e293b",
                      }}
                    />
                  ))}
                </div>
                <span className="text-[#64748b] text-xs font-medium">{step}/4</span>
              </div>

              <h3 className="text-lg font-bold text-white mb-6 text-center">
                {questions[step - 1].question}
              </h3>

              <div className="space-y-3">
                {questions[step - 1].options.map((option, i) => {
                  const Icon = option.icon;
                  return (
                    <motion.button
                      key={i}
                      data-testid={`quiz-option-${step}-${i}`}
                      onClick={() => handleAnswer(option.value)}
                      className="w-full flex items-center gap-4 p-4 rounded-xl border border-white/10 bg-white/[0.02] hover:border-[#06b6d4]/40 hover:bg-[#06b6d4]/5 transition-all text-left group"
                      whileHover={{ scale: 1.01, x: 4 }}
                      whileTap={{ scale: 0.99 }}
                    >
                      <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center group-hover:border-[#06b6d4]/30 transition-colors shrink-0">
                        <Icon size={18} className="text-[#64748b] group-hover:text-[#06b6d4] transition-colors" />
                      </div>
                      <div>
                        <span className="text-white text-sm font-semibold block">{option.label}</span>
                        <span className="text-[#64748b] text-xs">{option.desc}</span>
                      </div>
                      <ArrowRight size={14} className="ml-auto text-[#334155] group-hover:text-[#06b6d4] transition-colors shrink-0" />
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Result */}
          {step === 5 && result && (
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="p-8 text-center"
            >
              {(() => {
                const r = resultData[result];
                const Icon = r.icon;
                return (
                  <>
                    <motion.div
                      className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-5"
                      style={{ backgroundColor: `${r.color}15`, border: `2px solid ${r.color}40` }}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", delay: 0.2 }}
                    >
                      <Icon size={36} style={{ color: r.color }} />
                    </motion.div>

                    <p className="text-[#64748b] text-xs uppercase tracking-widest font-semibold mb-2">
                      {t("quiz_recommend")}
                    </p>
                    <h3 className="text-2xl font-bold text-white mb-1">{r.title}</h3>
                    <p className="text-sm mb-6" style={{ color: r.color }}>{r.subtitle}</p>

                    <div className="text-left space-y-2.5 mb-8 max-w-md mx-auto">
                      {r.reasons.map((reason, i) => (
                        <motion.div
                          key={i}
                          className="flex items-start gap-3"
                          initial={{ opacity: 0, x: -15 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.3 + i * 0.1 }}
                        >
                          <CheckCircle2 size={14} className="mt-0.5 shrink-0" style={{ color: r.color }} />
                          <span className="text-[#94a3b8] text-sm">{reason}</span>
                        </motion.div>
                      ))}
                    </div>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                      <motion.button
                        data-testid="quiz-get-info-btn"
                        onClick={() => setStep(6)}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-bold text-sm"
                        style={{ backgroundColor: r.color }}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                      >
                        {t("quiz_more_info")}
                        <ArrowRight size={14} />
                      </motion.button>
                      <button
                        data-testid="quiz-restart-btn"
                        onClick={restart}
                        className="inline-flex items-center gap-2 text-[#64748b] hover:text-white text-sm transition-colors"
                      >
                        <RotateCcw size={13} />
                        {t("quiz_restart")}
                      </button>
                    </div>
                  </>
                );
              })()}
            </motion.div>
          )}

          {/* Lead Form */}
          {step === 6 && (
            <motion.div
              key="lead"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              className="p-8"
            >
              <h3 className="text-lg font-bold text-white mb-1 text-center">{t("quiz_form_title")}</h3>
              <p className="text-[#64748b] text-sm mb-6 text-center">
                {t("quiz_form_subtitle")}
              </p>

              <form onSubmit={handleSubmitLead} className="space-y-3 max-w-sm mx-auto" data-testid="quiz-lead-form">
                <input
                  type="text"
                  required
                  placeholder="Tu nombre"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-[#0a0f1e] border border-white/10 text-white text-sm focus:border-[#06b6d4] focus:outline-none"
                  data-testid="quiz-input-name"
                />
                <input
                  type="email"
                  required
                  placeholder="tu@email.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-[#0a0f1e] border border-white/10 text-white text-sm focus:border-[#06b6d4] focus:outline-none"
                  data-testid="quiz-input-email"
                />
                <input
                  type="tel"
                  placeholder="Telefono (opcional)"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-[#0a0f1e] border border-white/10 text-white text-sm focus:border-[#06b6d4] focus:outline-none"
                  data-testid="quiz-input-phone"
                />
                <motion.button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-[#06b6d4] to-[#10b981] text-white font-bold text-sm flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid="quiz-submit-btn"
                >
                  {loading ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <>
                      <Send size={14} />
                      {t("quiz_submit")}
                    </>
                  )}
                </motion.button>
              </form>

              <button
                onClick={() => setStep(5)}
                className="block mx-auto mt-4 text-[#64748b] hover:text-white text-xs transition-colors"
              >
                {t("quiz_back_result")}
              </button>
            </motion.div>
          )}

          {/* Thank you */}
          {step === 7 && (
            <motion.div
              key="thanks"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-8 text-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 0.1 }}
              >
                <CheckCircle2 size={48} className="mx-auto mb-4 text-[#10b981]" />
              </motion.div>
              <h3 className="text-xl font-bold text-white mb-2">{t("quiz_success_title")}</h3>
              <p className="text-[#94a3b8] text-sm mb-6">
                {t("quiz_success_msg")}
              </p>
              <button
                onClick={restart}
                data-testid="quiz-done-btn"
                className="inline-flex items-center gap-2 text-[#06b6d4] hover:text-white text-sm font-semibold transition-colors"
              >
                <RotateCcw size={13} />
                {t("quiz_redo")}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default VersionQuiz;
